import { useEffect, useMemo, useState, type RefObject } from 'react'
import { useBackend } from '../lib/session'
import type { Block, ImageBlock, Thought, ThoughtLikeBlock } from '../lib/types'
import { isThoughtLike, usedThoughtIds } from '../lib/blocks'
import { dayName, startOfDay, thoughtKind, thoughtStamp, weekdayDate } from '../lib/text'
import { chip, errorText } from './ui'
import { PanelEmpty as Empty, PanelHeader, panelCard } from './SidePanel'

export const THOUGHT_MIME = 'application/x-burrow-thought'
/** Set beside THOUGHT_MIME when the thought is a photo (its JSON can't be read until the drop). */
export const PHOTO_MIME = 'application/x-burrow-photo'

type Filter = 'all' | 'day' | 'used'

/** How a saved thought appears in the writing, for the "In this writing" list. */
const USED_AS: Record<ThoughtLikeBlock['type'] | 'image', string> = {
  thought: 'IN THE COLUMN',
  margin: 'MARGIN NOTE',
  unfold: 'UNFOLD',
  image: 'PHOTO',
}
const DAYS = 7

interface Props {
  blocks: Block[]
  totalCount: number | null
  dayCounts: number[]
  /** Nothing written yet — the panel opens on today, the reflection starting point. */
  untouched: boolean
  searchRef?: RefObject<HTMLInputElement | null>
  /** A search from Burrow's nav bar: the panel opens on it, across all thoughts. */
  seedQuery?: { text: string; at: number } | null
  onInsert: (thought: Thought) => void
  onReflectDay: (daysAgo: number) => void
  onShowUsed: (thoughtId: string) => void
  onClose: () => void
}

function isLinkOnly(t: Thought): boolean {
  const body = t.body.trim()
  return !!t.source_url && (body === '' || body === t.source_url)
}

export function ThoughtsPanel({ blocks, totalCount, dayCounts, untouched, searchRef, seedQuery, onInsert, onReflectDay, onShowUsed, onClose }: Props) {
  const backend = useBackend()
  const [filter, setFilter] = useState<Filter>('all')
  const [chosen, setChosen] = useState(false)
  const [day, setDay] = useState(0)
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<Thought[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const used = useMemo(() => usedThoughtIds(blocks), [blocks])

  useEffect(() => {
    if (!chosen && untouched && (dayCounts[0] ?? 0) > 0) setFilter('day')
  }, [chosen, untouched, dayCounts])

  useEffect(() => {
    if (filter === 'used') return
    let alive = true
    const t = setTimeout(
      () => {
        backend
          .listThoughts({ search: query.trim() || undefined, day: filter === 'day' ? day : undefined, limit: 60 })
          .then((r) => {
            if (!alive) return
            setItems(r)
            setError(null)
          })
          .catch((e: unknown) => alive && setError(errorText(e)))
      },
      query ? 200 : 0,
    )
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [backend, filter, day, query])

  const pick = (f: Filter) => {
    setChosen(true)
    setFilter(f)
  }
  useEffect(() => {
    if (!seedQuery) return
    setChosen(true)
    setFilter('all')
    setQuery(seedQuery.text)
  }, [seedQuery])
  const dayCount = dayCounts[day] ?? 0
  // Every block made from a saved thought: column thoughts, margin notes, pins, unfolds and photos.
  const snapshots = blocks.filter(
    (b): b is ThoughtLikeBlock | ImageBlock => (isThoughtLike(b) || b.type === 'image') && !!b.thoughtId,
  )
  const snapshotText = (b: ThoughtLikeBlock | ImageBlock) => (b.type === 'image' ? b.caption : b.text)
  const q = query.trim().toLowerCase()
  const shownSnapshots = snapshots.filter((b) => !q || snapshotText(b).toLowerCase().includes(q))

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-col gap-3 px-[18px] pb-3 pt-[18px]">
        <PanelHeader closeLabel="Hide thoughts" onClose={onClose}>
          Thoughts
          {totalCount !== null && <span className="font-medium text-muted-foreground"> · {totalCount.toLocaleString()} saved</span>}
        </PanelHeader>
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={filter === 'day' ? `Search ${dayName(day).toLowerCase()}’s thoughts` : 'Search all thoughts'}
          aria-label="Search thoughts"
          className="w-full rounded-xl border border-border/40 bg-card/60 px-3.5 py-2 text-[13px] text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-accent/40 focus:bg-card"
        />
        <div className="wr-chip-row flex gap-1.5">
          <button type="button" aria-pressed={filter === 'all'} onClick={() => pick('all')} className={chip(filter === 'all')}>
            All
          </button>
          <button type="button" aria-pressed={filter === 'day'} onClick={() => pick('day')} className={chip(filter === 'day')}>
            {dayName(day)} · {dayCount}
          </button>
          <button type="button" aria-pressed={filter === 'used'} onClick={() => pick('used')} className={chip(filter === 'used')}>
            In this writing · {used.size}
          </button>
        </div>
        {filter === 'day' && (
          <DayPicker day={day} setDay={setDay} dayCounts={dayCounts} onReflect={() => onReflectDay(day)} />
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 pb-5 pt-1">
        {untouched && filter === 'day' && day === 0 && dayCount > 0 && (
          <div className="flex items-center justify-between gap-2.5 rounded-2xl bg-accent/15 px-4 py-3 text-[13px] font-medium text-primary">
            <span>
              {dayCount} thought{dayCount === 1 ? '' : 's'} from today. Make this a reflection?
            </span>
            <button type="button" onClick={() => onReflectDay(0)} className="whitespace-nowrap font-bold">
              Add all →
            </button>
          </div>
        )}

        {filter === 'used' ? (
          shownSnapshots.length === 0 ? (
            <Empty>{snapshots.length ? 'Nothing matches.' : 'No thoughts in this writing yet.'}</Empty>
          ) : (
            shownSnapshots.map((b) => (
              <div key={b.id} className={`${panelCard} bg-card/60`}>
                {b.capturedAt && (
                  <div className="text-[11px] font-medium text-muted-foreground">
                    {thoughtStamp(b.capturedAt)}
                    {b.kind ? ` · ${b.kind}` : ''}
                  </div>
                )}
                <p className="m-0 line-clamp-3 font-display text-[14px]/[1.6] text-foreground/75">{snapshotText(b)}</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground">{b.type === 'margin' && b.span ? 'PINNED' : USED_AS[b.type]}</span>
                  <button type="button" onClick={() => onShowUsed(b.thoughtId!)} className="text-[13px] font-medium text-muted-foreground hover:text-primary">
                    Used above ↑
                  </button>
                </div>
              </div>
            ))
          )
        ) : error ? (
          <Empty>{error}</Empty>
        ) : items === null ? (
          <Empty>Loading thoughts…</Empty>
        ) : items.length === 0 ? (
          <Empty>{q ? 'Nothing matches.' : filter === 'day' ? `Nothing saved ${dayName(day).toLowerCase()}.` : 'No thoughts yet.'}</Empty>
        ) : (
          items.map((t) => (
            <PanelCard key={t.id} thought={t} used={used.has(t.id)} onInsert={() => onInsert(t)} onShowUsed={() => onShowUsed(t.id)} />
          ))
        )}
        <p className="m-0 pt-1.5 text-center text-[11px] font-medium text-muted-foreground/70">Drag a thought into the text, or click Insert</p>
      </div>
    </div>
  )
}

function DayPicker({
  day,
  setDay,
  dayCounts,
  onReflect,
}: {
  day: number
  setDay: (d: number) => void
  dayCounts: number[]
  onReflect: () => void
}) {
  const count = dayCounts[day] ?? 0
  const rel = day === 0 ? 'Today' : day === 1 ? 'Yesterday' : `${day} days ago`
  const arrow = 'flex size-7 items-center justify-center rounded-xl border border-border/40 text-[14px] text-muted-foreground hover:text-foreground disabled:opacity-30'
  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-card-edge bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <button type="button" aria-label="Previous day" disabled={day >= DAYS - 1} onClick={() => setDay(Math.min(DAYS - 1, day + 1))} className={arrow}>
          ‹
        </button>
        <div className="flex min-w-0 flex-col items-center gap-px">
          <span className="truncate font-display text-[14px] text-primary">{weekdayDate(startOfDay(day))}</span>
          <span className="text-[11px] font-medium text-muted-foreground">
            {rel} · {count} thought{count === 1 ? '' : 's'}
          </span>
        </div>
        <button type="button" aria-label="Next day" disabled={day === 0} onClick={() => setDay(Math.max(0, day - 1))} className={arrow}>
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: DAYS }, (_, i) => DAYS - 1 - i).map((n) => {
          const c = dayCounts[n] ?? 0
          const active = n === day
          const date = startOfDay(n)
          return (
            <button
              key={n}
              type="button"
              aria-label={`${weekdayDate(date)}, ${c} thoughts`}
              aria-pressed={active}
              onClick={() => setDay(n)}
              className={`flex flex-col items-center gap-1 rounded-lg border pb-[5px] pt-1.5 text-[11px] font-semibold ${
                active ? 'border-accent/30 bg-accent/15 text-accent' : c ? 'border-border/40 bg-card text-foreground' : 'border-border/40 text-muted-foreground/70'
              }`}
            >
              <span>{date.toLocaleDateString('en-US', { weekday: 'narrow' })}</span>
              <span className={`size-[5px] rounded-full ${c ? 'bg-accent' : 'bg-transparent'}`} />
            </button>
          )
        })}
      </div>
      {count > 0 && (
        <button type="button" onClick={onReflect} className="rounded-xl bg-accent/15 py-2 text-center text-[13px] font-medium text-accent transition-colors hover:bg-accent/25">
          Reflect on this day · add all {count}
        </button>
      )}
    </div>
  )
}

function PanelCard({ thought, used, onInsert, onShowUsed }: { thought: Thought; used: boolean; onInsert: () => void; onShowUsed: () => void }) {
  const backend = useBackend()
  const [thumb, setThumb] = useState<string | null>(null)
  useEffect(() => {
    if (!thought.image_path) return
    let alive = true
    backend
      .thoughtImageUrl(thought.image_path)
      .then((u) => alive && setThumb(u))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [backend, thought.image_path])

  const action = used ? 'Used above ↑' : thought.image_path ? 'Insert photo' : isLinkOnly(thought) ? 'Insert as card' : 'Insert as quote'
  const text = thought.body.trim() && !isLinkOnly(thought) ? thought.body : thought.title || thought.source_title || thought.source_url || ''
  return (
    <div
      draggable={!used}
      onDragStart={(e) => {
        e.dataTransfer.setData(THOUGHT_MIME, JSON.stringify(thought))
        // A photo wraps where it's dropped, never makes a column; the page can tell from the types while dragging.
        if (thought.image_path) e.dataTransfer.setData(PHOTO_MIME, '1')
        e.dataTransfer.effectAllowed = 'copy'
      }}
      className={`${panelCard} ${used ? 'opacity-60' : 'cursor-grab hover:shadow-lg hover:shadow-accent/5 active:cursor-grabbing'}`}
    >
      <div className="text-[11px] font-medium text-muted-foreground">
        {thoughtStamp(thought.created_at)} · {thoughtKind(thought)}
      </div>
      {thumb && <img src={thumb} alt="" className="h-28 w-full rounded-xl object-cover" />}
      <p className="m-0 line-clamp-4 font-display text-[14px]/[1.6] text-foreground/85">{text}</p>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={used ? onShowUsed : onInsert}
          className={`text-[13px] font-medium ${used ? 'text-muted-foreground hover:text-primary' : 'text-primary hover:underline'}`}
        >
          {action}
        </button>
      </div>
    </div>
  )
}
